import React, { useState } from 'react';
import { SectionCard } from '../layout/memberPrimitives';
import Button from '../ui/Button';
import type { FeedbackChatBootstrap } from '../../types/feedbackChat';

/**
 * The blocking notice shown before a member can send anything.
 *
 * BLOCKING IS DELIBERATE, not a dark pattern in reverse. This routes free text
 * to a third-party processor and may surface health disclosures, so the
 * acknowledgement is a recorded, versioned consent event rather than an ambient
 * banner someone can scroll past. It re-prompts when the copy version changes,
 * the same way the analytics banner does.
 *
 * ALL COPY COMES FROM THE SERVER. Nothing here is hardcoded, so clinician-signed
 * wording can change without shipping a client build — and so a reviewer only
 * has one file to check (functions/chat/prompts/approvedCopy.js).
 */

interface Props {
  bootstrap: FeedbackChatBootstrap;
  onAcknowledge: () => Promise<void>;
}

export default function FeedbackDisclaimerCard({ bootstrap, onAcknowledge }: Props) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const disclaimer = bootstrap.disclaimer;
  if (!disclaimer) return null;

  const handleAcknowledge = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onAcknowledge();
    } catch {
      setError('We could not record that just now. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <SectionCard eyebrow="Please read" title={disclaimer.title}>
      <ul className="flex flex-col gap-3 mb-5" style={{ listStyle: 'none', padding: 0 }}>
        {disclaimer.body.map((line) => (
          <li
            key={line}
            className="text-sm md:text-base leading-relaxed pl-4 relative"
            style={{ color: 'var(--melio-ink)' }}
          >
            <span
              aria-hidden="true"
              className="absolute left-0 top-[0.6em] rounded-full"
              style={{ width: 4, height: 4, backgroundColor: 'var(--melio-olive)' }}
            />
            {line}
          </li>
        ))}
      </ul>

      <label
        className="flex items-start gap-3 mb-5 cursor-pointer rounded-2xl p-3"
        style={{ backgroundColor: 'var(--melio-cream)' }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="mt-1"
          style={{ width: 16, height: 16, accentColor: 'var(--melio-olive-deep)' }}
        />
        <span className="text-sm leading-relaxed" style={{ color: 'var(--melio-ink)' }}>
          I understand this is an AI assistant, that it is not checked around the clock,
          and that it is not a substitute for medical advice.
        </span>
      </label>

      {error && (
        <p className="text-sm mb-3" role="alert" style={{ color: 'var(--melio-terracotta-deep)' }}>{error}</p>
      )}

      <Button
        variant="primary"
        onClick={handleAcknowledge}
        disabled={!checked || submitting}
      >
        {submitting ? 'One moment…' : disclaimer.ackLabel}
      </Button>
    </SectionCard>
  );
}
